import { useState } from "react";
import { Link } from "react-router-dom";

import AppShell, { EmptyState, ErrorNote } from "@/components/AppShell";
import ComparatorBar from "@/components/ComparatorBar";
import { CyaProgressCard } from "@/components/CyaProgressCard";
import NumericInput from "@/components/NumericInput";
import ProvenanceLine from "@/components/ProvenanceLine";
import ReadingCoverLine from "@/components/ReadingCoverLine";
import ReadingDueCard from "@/components/ReadingDueCard";
import RetestCard from "@/components/RetestCard";
import SeasonOpeningCard from "@/components/SeasonOpeningCard";
import SolarCoverToggle, { openCover } from "@/components/SolarCoverToggle";
import SequenceNote from "@/components/SequenceNote";
import SuggestionCard from "@/components/SuggestionCard";
import { ApiError, api } from "@/lib/api";
import { relativeTime, useAsync, useVesselPath } from "@/lib/hooks";
import { FULL_PARAMS, PARAMS, type ParamKey, formatValue } from "@/lib/params";
import {
  judgedValue,
  remainingBlocked,
  retestPlan,
  stateOf,
  testedValue,
} from "@/lib/provenance";
import type {
  AttentionItem,
  Equipment,
  EventList,
  CyaProgress,
  OvernightLoss,
  RainSuggestion,
  ParamEvaluation,
  Vessel,
  Reading,
  ReadingEvaluation,
  Saturation,
  SeasonOpening,
  Suggestions,
} from "@/types";

/**
 * The "now" screen: is the water OK, and what should I add?
 *
 * Ordered by what you came here to find out. Actions sit above the numbers,
 * because on most visits the numbers are only there to justify the actions.
 */
export default function Home() {
  const vp = useVesselPath();
  const pool = useAsync<Vessel>(() => api.get<Vessel>(""), []);
  const latest = useAsync<Reading | null>(
    () =>
      api.get<Reading>("/api/readings/latest").catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }),
    [],
  );

  const cyaProgress = useAsync<CyaProgress>(
    () => api.get<CyaProgress>("/api/cya-progress"),
    [],
  );
  const attention = useAsync<AttentionItem[]>(
    () => api.get<AttentionItem[]>("/api/attention"),
    [],
  );

  // Renders nothing unless a season is open with steps outstanding, so the
  // failure case is an absent card rather than a broken dashboard.
  const opening = useAsync<SeasonOpening | null>(
    () => api.get<SeasonOpening>("/api/season-opening").catch(() => null),
    [],
  );

  // Both only feed the solar cover control, and both fail to an absent control
  // rather than a broken dashboard: a cover you cannot toggle from here is a
  // minor inconvenience with a full event dialog two taps away.
  const covers = useAsync<Equipment[]>(
    () =>
      api
        .get<Equipment[]>("/api/equipment")
        .then((items) => items.filter((item) => item.category === "cover"))
        .catch(() => []),
    [],
  );
  const openSpans = useAsync<EventList | null>(
    () => api.get<EventList>("/api/events?open_only=true").catch(() => null),
    [],
  );

  // Weather is decoration, so a failure here must never break the dashboard.
  const rain = useAsync<RainSuggestion[]>(
    () => api.get<RainSuggestion[]>("/api/weather/rain-suggestions").catch(() => []),
    [],
  );

  // Not gated on a reading id: the overnight check is about a *pair* of
  // readings, so it has an answer — including the "no pair yet" one — whether
  // or not the latest reading measured chlorine.
  const overnight = useAsync<OvernightLoss | null>(
    () => api.get<OvernightLoss>("/api/overnight-loss").catch(() => null),
    [],
  );

  const readingId = latest.data?.id;

  const evaluation = useAsync<ReadingEvaluation | null>(
    () =>
      readingId
        ? api.get<ReadingEvaluation>(`/api/readings/${readingId}/evaluation`)
        : Promise.resolve(null),
    [readingId],
  );

  const suggestions = useAsync<Suggestions | null>(
    () =>
      readingId
        ? api.get<Suggestions>(`/api/readings/${readingId}/suggestions`)
        : Promise.resolve(null),
    [readingId],
  );

  // What the water has done since it was last tested. Null on the ordinary day
  // when nothing has been logged since the reading, which is when this whole
  // screen looks exactly as it always did.
  const retest = retestPlan(suggestions.data?.parameters);

  /**
   * Where the re-test sits in "Do this, in order" — which depends on *what* it
   * covers, not on a fixed position.
   *
   * A superseded chlorine reading outranks everything: FC is the parameter this
   * whole app treats as primary, an unknown sanitiser level is a safety
   * question, and sizing anything else while it is unknown is guessing. A
   * superseded pH or TA is not that. Muriatic acid supersedes both and leaves
   * chlorine untouched, so the app goes on offering a correctly-sized chlorine
   * dose — and leading with "re-test" there argues against the one thing that
   * needed doing, which at FC below its floor is the wrong way round.
   *
   * The re-test can never be a *prerequisite* for a dose listed beside it: any
   * suggestion still on the list was sized off figures nothing has touched,
   * because the ones sized off superseded figures are withheld server-side
   * before this screen sees them. So this is about urgency, not dependency.
   */
  const retestIsUrgent =
    retest?.rows.some(
      (row) => row.parameter === "fc" || row.parameter === "cc",
    ) ?? false;

  const retestCard = retest ? (
    <RetestCard
      plan={retest}
      interferencePpm={evaluation.data?.ph_interference_ppm}
      // Where chlorine will be when the sample is drawn, not where it was when
      // the last one was: a gallon logged since the test is exactly the thing
      // that pushes a borderline reading past what phenol red can cope with.
      // Projected first, then measured, then the last figure anyone saw.
      chlorine={
        evaluation.data?.parameters.fc?.projected_value ??
        evaluation.data?.parameters.fc?.value ??
        evaluation.data?.parameters.fc?.last_measured_value ??
        null
      }
    />
  ) : null;

  // "Can't say yet" keeps every reason the re-test card did not already
  // account for. Left unfiltered, a superseded chlorine reading would state its
  // one fact twice — once as the step to take and once as a complaint — and two
  // statements of one problem read as two problems.
  const blocked = retest
    ? remainingBlocked(suggestions.data?.blocked ?? [], retest.notes)
    : (suggestions.data?.blocked ?? []);

  // The chlorine reading has outlived the room it left above the floor.
  //
  // Suppressed while a re-test is already on the list: that one fires when
  // something was *added* since the test and this one when nothing was, so they
  // are answering the same question from two directions and stacking both would
  // put two "go and test" cards at the top of one screen. The server withholds
  // this in that case too; the check is repeated here because a stale response
  // must degrade to a missing card, never to a duplicated instruction.
  const readingDue =
    retest === null && suggestions.data?.reading_due?.due
      ? suggestions.data.reading_due
      : null;

  /** The label of a chemical this one must not meet, for the card's own line. */
  const partnerFor = (chemical: string): string | undefined => {
    const pair = suggestions.data?.sequence?.hazards.find((h) =>
      h.chemicals.includes(chemical),
    );
    const other = pair?.chemicals.find((c) => c !== chemical);
    return suggestions.data?.suggestions.find((s) => s.chemical === other)
      ?.chemical_label;
  };

  return (
    <AppShell
      title={pool.data?.name ?? "Pool"}
      subtitle={
        latest.data
          ? `Last tested ${relativeTime(latest.data.taken_at)}`
          : undefined
      }
      action={
        <Link to={vp("/log")} className="btn btn-primary px-3 py-2 text-sm">
          Log
        </Link>
      }
    >
      {latest.error && <ErrorNote message={latest.error} />}

      {/* Attention items sit above everything: they are the reason to have
          opened the app on a day when nothing else changed. */}
      {attention.data && attention.data.length > 0 && (
        <ul className="mb-6 space-y-2">
          {attention.data.map((item) => (
            <AttentionRow key={item.key} item={item} />
          ))}
        </ul>
      )}

      {/* Below the attention items but above everything else: a campaign in
          progress is context for the numbers, not an alert in its own right.
          Renders nothing once CYA is at goal. */}
      {cyaProgress.data && (
        <div className="mb-6">
          <CyaProgressCard progress={cyaProgress.data} />
        </div>
      )}

      {/* Above the rain suggestions and below the CYA campaign: opening the
          pool is the frame everything else on the screen sits inside for the
          week or so it runs, and it is the answer to a dashboard that would
          otherwise be blank because a season open supersedes every reading. */}
      {opening.data?.active && (
        <div className="mb-6">
          <SeasonOpeningCard opening={opening.data} />
        </div>
      )}

      {covers.data && covers.data.length > 0 && (
        <div className="mb-6">
          <SolarCoverToggle
            covers={covers.data}
            open={openCover(openSpans.data)}
            onChanged={() => {
              openSpans.reload();
              // The span is chart context today and may become chemistry — a
              // cover blocks the UV that drives chlorine loss. Refreshed rather
              // than left stale so it is already correct if that lands.
              suggestions.reload();
            }}
          />
        </div>
      )}

      {rain.data && rain.data.length > 0 && (
        <ul className="mb-6 space-y-2">
          {rain.data.map((item) => (
            <RainRow
              key={item.day}
              item={item}
              onLogged={() => rain.reload()}
            />
          ))}
        </ul>
      )}

      {!latest.loading && latest.data === null && (
        <EmptyState
          title="Nothing logged yet"
          body="Test the water and enter the numbers. The app starts working from your first reading."
          action={
            <Link to={vp("/log")} className="btn btn-primary">
              Log a reading
            </Link>
          }
        />
      )}

      {latest.data && (
        <div className="space-y-6">
          {/* --- What to do ---
              A re-test leads the list when something has been added since the
              reading: it is the step everything else waits on, and it is what
              stands in the place a withheld dose used to occupy. */}
          {suggestions.data &&
            (retest !== null ||
              readingDue !== null ||
              suggestions.data.suggestions.length > 0) && (
              <section>
                <h2 className="unit mb-2">Do this, in order</h2>
                {/* Above the list, because it is a fact about doing both rather
                  than about either one. */}
                {suggestions.data.sequence && (
                  <SequenceNote sequence={suggestions.data.sequence} />
                )}
                <ol className="space-y-2">
                  {/* First among the chemicals because a dose sized off a
                    reading whose cover has run out is sized off a number nobody
                    can vouch for any more. */}
                  {readingDue && <ReadingDueCard due={readingDue} />}
                  {retestIsUrgent && retestCard}
                  {suggestions.data.suggestions.map((suggestion) => (
                    <SuggestionCard
                      key={`${suggestion.chemical}-${suggestion.parameter}`}
                      suggestion={suggestion}
                      incompatibleWith={partnerFor(suggestion.chemical)}
                      readingId={latest.data!.id}
                      onLogged={() => suggestions.reload()}
                    />
                  ))}
                  {!retestIsUrgent && retestCard}
                </ol>
              </section>
            )}

          {/* "Everything measured is in range" has to mean it. A panel with a
              superseded figure in it has measurements nobody has checked since
              they were moved, so the all-clear is not ours to give.

              Nor is it ours to give once the chlorine reading has outlived its
              cover. The figures on this screen were all in range when they were
              read, and saying so directly beneath a card explaining that the
              chlorine has had a day to burn off would be a contradiction the
              owner has to resolve — and the comfortable half of it is the wrong
              one. */}
          {suggestions.data &&
            suggestions.data.suggestions.length === 0 &&
            blocked.length === 0 &&
            readingDue === null &&
            retest === null && (
              <section className="card px-4 py-5 text-center">
                <p className="font-medium text-(--color-status-ok)">
                  Nothing to add
                </p>
                <p className="mt-1 text-sm text-(--color-ink-muted)">
                  Everything measured is in range.
                </p>
              </section>
            )}

          {/* Saying why a suggestion is missing beats an ambiguous empty list. */}
          {suggestions.data && blocked.length > 0 && (
            <section className="card p-4">
              <h2 className="unit">Can't say yet</h2>
              <ul className="mt-2 space-y-1.5">
                {blocked.map((reason) => (
                  <li key={reason} className="text-sm text-(--color-ink-muted)">
                    {reason}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* --- Where everything sits --- */}
          <section>
            {/* The way out of this section sits at the top of it rather than
                at the bottom of the page. It is a sideways move to the same
                reading in more detail, not the next thing to do — and a
                full-width primary-looking button at the end of the screen read
                as the latter. */}
            <div className="mb-2 flex items-baseline justify-between gap-3">
              <h2 className="unit">Latest reading</h2>
              <Link
                to={vp(`/history/${latest.data.id}`)}
                className="shrink-0 text-sm text-(--color-ink-muted) underline underline-offset-4"
              >
                See full reading →
              </Link>
            </div>

            {/* How much longer these numbers describe the water, when the
                answer is "a while yet". Above the panel rather than below it:
                it qualifies everything in the panel, and a shelf life printed
                after the figures it applies to is read second or not at all.
                The spent case is a step in the list above instead. */}
            {suggestions.data?.reading_due && (
              <ReadingCoverLine due={suggestions.data.reading_due} />
            )}

            <div className="mt-2 card divide-y divide-(--color-border-subtle)">
              {FULL_PARAMS.map((key) => (
                <ParameterRow
                  key={key}
                  paramKey={key}
                  evaluation={evaluation.data?.parameters[key]}
                />
              ))}
            </div>
          </section>

          {/* --- Overnight chlorine loss ---
              Below the numbers rather than above them: this is a diagnosis of
              the routine, not a reading. It only becomes an alert when it
              fails, and then it does so as an attention item at the top. */}
          {overnight.data && <OvernightCard result={overnight.data} />}

          {/* --- Saturation index --- */}
          {evaluation.data?.saturation_index && (
            <SaturationCard saturation={evaluation.data.saturation_index} />
          )}

          {evaluation.data?.saturation_note && (
            <p className="text-xs text-(--color-ink-faint)">
              {evaluation.data.saturation_note}
            </p>
          )}
        </div>
      )}
    </AppShell>
  );
}

/**
 * One measurement, its target, and how far it can be trusted.
 *
 * Four provenances have to be told apart at a glance, on a 390px screen, in
 * sunlight, with no legend — and without turning the ordinary day, when
 * everything was measured an hour ago, into anything other than what it was.
 * Two signals do it, both already in the row:
 *
 * 1. **Is the figure in its reagent colour?** `measured` and `carried` are, so
 *    they read as live numbers, because both may be judged against a target.
 *    `superseded` and `missing` are greyed, because neither may.
 * 2. **What does the row say in words?** Within each pair the second signal is
 *    text, not tint — "was 10.0" against "10.0", "carried · tested Jul 19"
 *    against silence, a figure against an em dash. Words survive sunlight and a
 *    greyscale screenshot; a fifth colour would not, and there is no room on
 *    this screen for a key explaining one.
 */
function ParameterRow({
  paramKey,
  evaluation,
}: {
  paramKey: ParamKey;
  evaluation?: ParamEvaluation;
}) {
  const meta = PARAMS[paramKey];
  const state = stateOf(evaluation);

  // Two different numbers, and conflating them is the bug this screen had. The
  // judged one is what may be compared to a target and goes null the moment
  // something moves it; the tested one is what the kit read and stays, because
  // "was 10.0" is unsayable without it.
  const value = judgedValue(evaluation);
  const tested = testedValue(evaluation);
  const shown = state === "superseded" ? tested : value;

  const ideal = evaluation?.target
    ? {
        low: Number(evaluation.target.target_low),
        high: Number(evaluation.target.target_high),
      }
    : null;

  // The outer acceptable bounds, kept apart from the aim band all the way to
  // the screen. Either may be null and free chlorine's maximum always is —
  // the published chart is minimum, target and SLAM, with no ceiling in it.
  const bounds = evaluation?.target
    ? {
        min:
          evaluation.target.minimum === null
            ? null
            : Number(evaluation.target.minimum),
        max:
          evaluation.target.maximum === null
            ? null
            : Number(evaluation.target.maximum),
      }
    : null;

  const status = evaluation?.status ?? "missing";
  const verdict =
    status === "low"
      ? "low"
      : status === "high"
        ? "high"
        : status === "ok"
          ? "in range"
          : null;

  return (
    // Grouped and named, so the qualifier lines, the bar and the provenance note
    // are announced as belonging to this measurement rather than as loose text
    // between two figures.
    <div role="group" aria-label={meta.full} className="px-4 py-3.5">
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium">{meta.full}</p>
          {ideal && (
            <p className="unit mt-0.5">
              <BandLabel ideal={ideal} bounds={bounds} />
              {/* Say when a number is projected rather than read off the
                  published chart, so a CYA-157 target is never mistaken for
                  an established figure. */}
              {evaluation?.target?.extrapolated && (
                <span className="ml-1.5 text-(--color-status-warn)">
                  projected
                </span>
              )}
            </p>
          )}
          <ProvenanceLine state={state} measuredAt={evaluation?.measured_at} />
          {/* Alkalinity is judged on carbonate once the cyanurate share is
              removed, so the tested number alone would not explain the
              verdict. */}
          {evaluation?.judged_value != null && (
            <p className="unit mt-0.5 text-(--color-status-warn)">
              judged on {evaluation.judged_value} carbonate
            </p>
          )}
        </div>
        <p className="flex items-baseline gap-1.5">
          {/* Past tense, in words. The number is still the most useful thing to
              show — "was 10.0" is what makes the note underneath legible — but
              it must not be readable as the level in the water right now. */}
          {state === "superseded" && shown !== null && (
            <span className="unit">was</span>
          )}
          <span
            className="figure text-3xl"
            style={{
              color: value === null ? "var(--color-ink-faint)" : meta.color,
            }}
          >
            {formatValue(shown, meta)}
          </span>
          <span className="unit">{meta.unit}</span>
        </p>
      </div>

      <div className="mt-2">
        <ComparatorBar
          // The bar plots only what may be judged, so a superseded parameter
          // gets the dashed "no reading" rule. Parking a marker at the last
          // measured position would draw a verdict on water that no longer
          // exists, and plotting the projection would draw one on a number
          // nobody tested.
          value={value}
          scaleMin={meta.scaleMin}
          scaleMax={meta.scaleMax}
          ideal={ideal}
          bounds={bounds}
          // The server's verdict wins — it judges against the outer acceptable
          // range, which is wider than the ideal zone drawn on the bar.
          status={status === "unknown_target" ? "missing" : status}
          color={meta.color}
          label={meta.full}
        />
      </div>

      {verdict && verdict !== "in range" && (
        <p
          className="mt-1.5 text-xs font-medium"
          style={{ color: `var(--color-status-${status})` }}
        >
          {verdict}
        </p>
      )}

      {evaluation?.note && (
        <p className="mt-1.5 text-xs text-(--color-ink-faint)">
          {evaluation.note}
        </p>
      )}
    </div>
  );
}

/**
 * The target band in words: the outer bounds, then the range to aim for.
 *
 * The row used to print "target 15.5–17.5" and nothing else, which left the
 * floor — the number that decides whether the water is protected — off the
 * screen entirely. At CYA 157 that floor is 12.5, and a reading of 13.0 is
 * half a ppm above it; without the figure there is no way to see that from the
 * dashboard.
 *
 * **Free chlorine has no ceiling and none is invented here.** The published
 * TFP chart is minimum, target and SLAM; there is no maximum in it, and the
 * numbers people half-remember as one — the 10 ppm cap for public pools — sit
 * *below* this pool's floor. So the label adapts to whichever bounds actually
 * exist rather than always printing a pair:
 *
 *     floor 12.5 · aim 15.5–17.5      free chlorine, no published ceiling
 *     7.2–7.8 · aim 7.4–7.6           pH, bounded both ends
 *     aim 0–0                         combined chlorine, no floor worth saying
 */
function BandLabel({
  ideal,
  bounds,
}: {
  ideal: { low: number; high: number };
  bounds: { min: number | null; max: number | null } | null;
}) {
  const aim = `aim ${ideal.low}–${ideal.high}`;
  const min = bounds?.min ?? null;
  const max = bounds?.max ?? null;

  if (min !== null && max !== null) return <>{`${min}–${max} · ${aim}`}</>;
  if (min !== null) return <>{`floor ${min} · ${aim}`}</>;
  if (max !== null) return <>{`ceiling ${max} · ${aim}`}</>;
  return <>{aim}</>;
}

/**
 * The Overnight Chlorine Loss Test, as a result or as an instruction.
 *
 * Deliberately quiet. A pass is a small grey line — it is the expected state,
 * and a green tick for "nothing is wrong" on a dashboard you check daily
 * becomes wallpaper. A failure is not shown loudly *here* either, because it
 * has already been raised as an urgent attention item at the top of the
 * screen; repeating it in red twice would read as two problems.
 *
 * The empty state carries the most weight of the three, and that is the point:
 * with no qualifying pair the app says what to test and when rather than
 * substituting a default. An invented overnight loss would be a fabricated
 * measurement, and this one is load-bearing — it is the difference between
 * "dose more tonight" and "you have algae, SLAM it".
 */
function OvernightCard({ result }: { result: OvernightLoss }) {
  const { loss } = result;

  return (
    <section className="card p-4">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="unit">Overnight chlorine loss</h2>
        {loss && (
          <p className="flex items-baseline gap-1.5">
            <span
              className="figure text-xl"
              style={{
                color:
                  result.passed === false
                    ? "var(--color-status-high)"
                    : "var(--color-reagent-dpd)",
              }}
            >
              {loss.ppm}
            </span>
            <span className="unit">ppm</span>
          </p>
        )}
      </div>

      {loss && (
        <p className="unit mt-1">
          {loss.from_label} · {loss.ppm} ppm lost · limit {result.limit_ppm}
        </p>
      )}

      <p className="mt-2 text-sm text-(--color-ink-muted)">{result.note}</p>
    </section>
  );
}

/**
 * Scale-versus-corrosion balance, read against this pool.
 *
 * The card used to be a number, a word, and one sentence keyed only on that
 * word — which at -0.32 told the owner of a vinyl pool with no heater that
 * their water was "hard on heaters and filter internals". The sentence was
 * fixed text for a whole third of the possible readings, so it could not vary
 * with how far past the line the index sat, and it could not know what was on
 * the pad.
 *
 * The order here is the house rule applied inside one card: **what to do
 * first**, reasoning after. Someone who opens the app worried about a red
 * number wants the answer, not the derivation, and on this pool the answer is
 * usually "not yet, and here is why" — which reads as reassuring only if it
 * arrives before the sentence about dissolving calcium, not after it.
 *
 * Caveats go last and stay faint. They bound a reassurance rather than raise an
 * alarm, and a caveat printed at the same weight as the advice is one nobody
 * finishes reading.
 */
function SaturationCard({ saturation }: { saturation: Saturation }) {
  const { advice } = saturation;

  return (
    <section className="card p-4">
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <h2 className="unit">Saturation index</h2>
          <p className="mt-0.5 text-sm capitalize">{saturation.verdict}</p>
        </div>
        <p className="figure text-2xl">{saturation.value}</p>
      </div>

      {advice && <p className="mt-3 text-sm">{advice.action}</p>}

      {/* The chemistry, then the pool. Both are "why", and neither is the
          thing to act on — so they sit under the advice at qualifier weight. */}
      <p className="mt-2 text-xs text-(--color-ink-muted)">
        {saturation.explanation}
        {advice && ` ${advice.exposure}`}
      </p>

      {advice && (
        <p className="mt-1.5 text-xs text-(--color-ink-muted)">
          {advice.margin}
        </p>
      )}

      {advice?.caveats.map((caveat) => (
        <p key={caveat} className="mt-1.5 text-xs text-(--color-ink-faint)">
          {caveat}
        </p>
      ))}

      {saturation.tds_was_assumed && (
        <p className="mt-1.5 text-xs text-(--color-ink-faint)">
          Calculated with an assumed TDS, since none was measured.
        </p>
      )}
    </section>
  );
}

const SEVERITY_COLOR: Record<AttentionItem["severity"], string> = {
  info: "var(--color-ink-faint)",
  warning: "var(--color-status-high)",
  urgent: "var(--color-status-high)",
};

function AttentionRow({ item }: { item: AttentionItem }) {
  return (
    <li className="card flex gap-3 p-4">
      <span
        aria-hidden
        className="mt-1 h-2 w-2 shrink-0 rounded-full"
        style={{ background: SEVERITY_COLOR[item.severity] }}
      />
      <div className="min-w-0">
        <p className="text-sm font-medium">{item.title}</p>
        <p className="mt-0.5 text-sm text-(--color-ink-muted)">{item.detail}</p>
      </div>
    </li>
  );
}

/**
 * A rain day the weather service saw and you haven't logged.
 *
 * **The amount is editable, and that is the point.** The figure Open-Meteo
 * returns is model output over a grid cell several kilometres across, not a
 * measurement — against a real gauge a mile away the two can differ by a factor
 * of ten for a summer thunderstorm, because that is how local rainfall is.
 *
 * The suggestion has always been a suggestion rather than an automatic record,
 * on the grounds that rain near the pool is not rain over it. But it only
 * half-honoured that: it asked you to confirm *whether* it rained, then wrote
 * the model's number down as *how much* — the part you are least able to vouch
 * for, and the part that decides things. Depth gates
 * `rain_event_threshold_inches`, which is what says whether a chlorine reading
 * still stands.
 *
 * So the model's figure is offered, pre-filled, and yours overrides it. What
 * gets stored says which it was: a number you typed is `manual`, one you
 * accepted is `weather_api`, and the log can be read honestly years later.
 */
function RainRow({
  item,
  onLogged,
}: {
  item: RainSuggestion;
  onLogged: () => void;
}) {
  const [inches, setInches] = useState(item.precip_inches);
  const [busy, setBusy] = useState(false);

  const edited = inches.trim() !== item.precip_inches;

  async function ignore() {
    setBusy(true);
    try {
      // Hidden, not logged: nothing is recorded as rain. Rain you did see is
      // still yours to log by hand from the events screen.
      await api.post(`/api/weather/rain-suggestions/${item.day}/dismiss`);
      onLogged();
    } finally {
      setBusy(false);
    }
  }

  async function log() {
    setBusy(true);
    try {
      await api.post("/api/events", {
        event_type: "rain",
        starts_at: new Date(`${item.day}T12:00:00Z`).toISOString(),
        payload: {
          event_type: "rain",
          inches: inches.trim() === "" ? null : inches.trim(),
          source: edited ? "manual" : "weather_api",
        },
      });
      onLogged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="card p-4">
      <p className="text-sm">{item.message}</p>
      <div className="mt-3 flex items-center gap-2">
        <NumericInput
          aria-label={`Inches of rain on ${item.day}`}
          className="field figure w-24"
          value={inches}
          onChange={setInches}
        />
        <span className="unit shrink-0">inches</span>
        <button
          type="button"
          onClick={ignore}
          disabled={busy}
          className="ml-auto shrink-0 px-2 py-2 text-sm text-(--color-ink-muted) hover:text-(--color-ink)"
        >
          Ignore
        </button>
        <button
          type="button"
          onClick={log}
          disabled={busy}
          className="btn btn-quiet shrink-0 px-3 py-2 text-sm"
        >
          {busy ? "Working…" : "Log it"}
        </button>
      </div>
      {edited && (
        <p className="mt-1.5 text-xs text-(--color-ink-faint)">
          Recorded as your own measurement rather than the forecast's.
        </p>
      )}
    </li>
  );
}
